// DynamicFormRenderer.jsx
// Place this alongside DynamicFormRenderer.module.css
// Dependencies: react-hook-form, react-signature-canvas, react-datepicker
// npm i react-hook-form react-signature-canvas react-datepicker

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import SignaturePad from "react-signature-canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./index.module.css";

/**
 * DynamicFormRenderer
 * - Uses a schema object (see exported `formSchema` below)
 * - Draft autosave to localStorage (key: draft:{formId})
 * - Supports field types: text, textarea, radio, checkbox, select, date, file, signature, array
 * - Logic in schema: showWhen (visibility) and compute.formula (derived fields)
 */

const operators = {
  equals: (a, b) => a === b,
  notEquals: (a, b) => a !== b,
  in: (a, b) => Array.isArray(b) && b.includes(a),
  notIn: (a, b) => Array.isArray(b) && !b.includes(a),
  truthy: (a) => !!a,
  falsy: (a) => !a,
};

function evalTemplateExpression(expr, values) {
  if (!expr) return null;
  const replaced = expr.replace(/\{([a-zA-Z0-9_]+)\}/g, (_, key) => {
    const v = values[key];
    if (v === undefined || v === null) return "null";
    if (typeof v === "number") return String(v);
    if (!isNaN(Number(v))) return String(Number(v));
    return JSON.stringify(v);
  });
  try {
    // sandboxed-ish: only expose Math
    // eslint-disable-next-line no-new-func
    const fn = new Function("Math", `return (${replaced});`);
    return fn(Math);
  } catch (e) {
    console.warn("Failed to evaluate expression", expr, e);
    return null;
  }
}

function evalShowWhen(showWhen, values) {
  if (!showWhen) return true;
  const conditions = Array.isArray(showWhen) ? showWhen : [showWhen];
  return conditions.every((cond) => {
    // support nested AND/OR groups
    if (cond.type === "group") {
      if (cond.operator === "OR") {
        return cond.conditions.some((c) => evalShowWhen(c, values));
      }
      return cond.conditions.every((c) => evalShowWhen(c, values));
    }
    const left = values[cond.field];
    const op = operators[cond.operator || "equals"];
    return op ? op(left, cond.value) : true;
  });
}

/* FileField - small wrapper */
function FileField({ name, control, label, accept, initialValue }) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={initialValue || null}
      render={({ field }) => (
        <div>
          <label className={styles.questionLabel}>{label}</label>
          <input
            className={styles.input}
            type="file"
            accept={accept}
            onChange={(e) => field.onChange(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          />
          {field.value && typeof field.value === "object" && field.value.name && (
            <div className={styles.helperText}>Selected: {field.value.name}</div>
          )}
          {field.value && typeof field.value === "string" && (
            <div className={styles.helperText}>
              Existing file: <a href={field.value} target="_blank" rel="noreferrer">Open</a>
            </div>
          )}
        </div>
      )}
    />
  );
}

/* ArrayField - repeatable list renderer */
function ArrayField({ field, setValue, getValues }) {
  const items = getValues()[field.name] || [];
  function addItem() {
    const newItems = [...items, {}];
    setValue(field.name, newItems);
  }
  function removeItem(idx) {
    const newItems = items.filter((_, i) => i !== idx);
    setValue(field.name, newItems);
  }
  function updateItem(idx, key, value) {
    const newItems = [...items];
    newItems[idx] = { ...(newItems[idx] || {}), [key]: value };
    setValue(field.name, newItems);
  }

  return (
    <div>
      {(items || []).map((it, idx) => (
        <div key={idx} className={styles.arrayItem}>
          <div className={styles.arrayHeader}>
            <div>Item {idx + 1}</div>
            <div className={styles.arrayControls}>
              <button type="button" className={styles.backButton} onClick={() => removeItem(idx)}>Remove</button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            {field.itemFields.map((f) => (
              <div key={f.name}>
                <label className={styles.label}>{f.label}</label>
                {f.type === "select" ? (
                  <select
                    className={styles.input}
                    defaultValue={(it && it[f.name]) || ""}
                    onChange={(e) => updateItem(idx, f.name, e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {f.options && f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input
                    className={styles.input}
                    defaultValue={(it && it[f.name]) || ""}
                    onChange={(e) => updateItem(idx, f.name, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 8 }}>
        <button type="button" className={styles.nextButton} onClick={addItem}>Add</button>
      </div>
    </div>
  );
}

export default function DynamicFormRenderer({
  schema,
  onSubmit = (d, meta) => console.log("submit", d, meta),
  initialData = null,
  mode = "create",
  formId = "default-form",
  autoSaveInterval = 1000,
  onSchemaGenerate = null,
}) {
  const { control, register, handleSubmit, watch, setValue, getValues, reset, formState } = useForm({
    mode: "onChange",
    defaultValues: initialData || {},
  });
  const allValues = watch();
  const signatureRefs = useRef({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const draftKey = `draft:${formId}`;
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // recompute computed fields
  useEffect(() => {
    if (!schema || !schema.sections) return;
    const computed = [];
    schema.sections.forEach((sec) => {
      (sec.fields || []).forEach((f) => {
        if (f.compute && f.compute.formula) computed.push({ name: f.name, formula: f.compute.formula });
      });
    });
    if (computed.length === 0) return;
    try {
      const values = getValues();
      computed.forEach((c) => {
        const val = evalTemplateExpression(c.formula, values);
        if (val !== null && val !== undefined) setValue(c.name, typeof val === "number" && !Number.isFinite(val) ? null : val);
      });
    } catch (e) {
      console.warn("Failed computing fields", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allValues, schema]);

  // load draft merged with initialData
  useEffect(() => {
    const draftRaw = localStorage.getItem(draftKey);
    if (draftRaw) {
      try {
        const draft = JSON.parse(draftRaw);
        const merged = { ...(initialData || {}), ...draft };
        reset(merged);
        return;
      } catch (e) {
        console.warn("Failed to parse draft", e);
      }
    }
    reset(initialData || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // autosave
  useEffect(() => {
    if (!autoSaveEnabled) return;
    const handler = setTimeout(() => {
      try {
        const values = getValues();
        const serializable = {};
        Object.keys(values).forEach((k) => {
          const v = values[k];
          if (v instanceof File) serializable[k] = { __file_name: v.name };
          else serializable[k] = v;
        });
        localStorage.setItem(draftKey, JSON.stringify(serializable));
        setLastSavedAt(new Date().toISOString());
      } catch (e) {
        console.warn("Failed to autosave draft", e);
      }
    }, autoSaveInterval);
    return () => clearTimeout(handler);
  }, [allValues, autoSaveEnabled]);

  const visibleSections = useMemo(() => {
    if (!schema || !schema.sections) return [];
    return schema.sections.filter((sec) => evalShowWhen(sec.showWhen, getValues()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, allValues]);

  function renderField(field) {
    if (!evalShowWhen(field.showWhen, getValues())) return null;
    const initialValue = (getValues && getValues()[field.name]) || (initialData ? initialData[field.name] : undefined);
    const validation = {};
    if (field.required) validation.required = true;
    if (field.validation) {
      if (field.validation.pattern) validation.pattern = new RegExp(field.validation.pattern);
      if (field.validation.min) validation.min = field.validation.min;
      if (field.validation.max) validation.max = field.validation.max;
    }

    switch (field.type) {
      case "text":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <div className={styles.inputWrapper}>
              <label className={styles.questionLabel}>
                {field.label} {field.required && <span className={styles.required}>*</span>}
              </label>
              <input className={styles.input} {...register(field.name, validation)} placeholder={field.placeholder} defaultValue={initialValue} />
              {formState.errors?.[field.name] && <div className={styles.errorMessage}>{formState.errors[field.name].message || "Required"}</div>}
            </div>
          </div>
        );

      case "textarea":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <div className={styles.inputWrapper}>
              <label className={styles.questionLabel}>{field.label}{field.required && <span className={styles.required}>*</span>}</label>
              <textarea className={styles.textarea} {...register(field.name)} defaultValue={initialValue} rows={field.rows || 4} />
            </div>
          </div>
        );

      case "radio":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <div className={styles.questionHeader}>
              <div className={styles.questionLabel}>{field.label}{field.required && <span className={styles.required}>*</span>}</div>
            </div>
            <div className={styles.optionsWrapper}>
              {field.options.map((o) => {
                const selected = initialValue === o.value;
                return (
                  <label key={o.value} className={`${styles.radioOption} ${selected ? styles.radioOptionSelected : ""}`}>
                    <input className={styles.radioInput} type="radio" value={o.value} {...register(field.name)} defaultChecked={selected} />
                    <span className={styles.radioCircle}><span className={styles.radioCircleInner} /></span>
                    <div className={styles.optionText}>{o.label}</div>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case "checkbox":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <label className={`${styles.checkboxOption} ${initialValue ? styles.checkboxOptionSelected : ""}`}>
              <input className={styles.checkboxInput} type="checkbox" {...register(field.name)} defaultChecked={!!initialValue} />
              <div className={styles.checkboxBox} /><div className={styles.optionText}>{field.label}</div>
            </label>
          </div>
        );

      case "select":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <div className={styles.inputWrapper}>
              <label className={styles.questionLabel}>{field.label}</label>
              <select className={styles.input} {...register(field.name)} defaultValue={initialValue || ""}>
                <option value="">-- Select --</option>
                {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        );

      case "date":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <div className={styles.inputWrapper}>
              <label className={styles.questionLabel}>{field.label}</label>
              <Controller control={control} name={field.name} defaultValue={initialValue || null} render={({ field: ctrlField }) => <DatePicker selected={ctrlField.value} onChange={(d) => ctrlField.onChange(d)} placeholderText={field.placeholder} />} />
            </div>
          </div>
        );

      case "file":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <FileField name={field.name} control={control} label={field.label} accept={field.accept} initialValue={initialValue} />
          </div>
        );

      case "signature":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <div className={styles.inputWrapper}>
              <label className={styles.questionLabel}>{field.label}</label>
              <div className={styles.signatureWrap}>
                <SignaturePad ref={(ref) => (signatureRefs.current[field.name] = ref)} canvasProps={{ className: styles.signatureCanvas }} />
              </div>
              {initialValue && typeof initialValue === "string" && <div className={styles.helperText}><img src={initialValue} alt="sig" style={{ maxWidth: 320 }} /></div>}
              <div style={{ marginTop: 8 }}>
                <button type="button" className={styles.backButton} onClick={() => signatureRefs.current[field.name] && signatureRefs.current[field.name].clear()}>Clear</button>
                <button type="button" className={styles.nextButton} onClick={() => setValue(field.name, signatureRefs.current[field.name] ? signatureRefs.current[field.name].toDataURL() : null)}>Save Signature</button>
                <input type="hidden" {...register(field.name)} />
              </div>
            </div>
          </div>
        );

      case "array":
        return (
          <div className={styles.questionWrapper} key={field.name}>
            <label className={styles.questionLabel}>{field.label}</label>
            <ArrayField field={field} setValue={setValue} getValues={getValues} />
          </div>
        );

      default:
        return null;
    }
  }

  async function submitHandler(values) {
    setIsSubmitting(true);
    Object.keys(signatureRefs.current).forEach((k) => {
      if (!values[k]) {
        const ref = signatureRefs.current[k];
        if (ref && !ref.isEmpty()) values[k] = ref.toDataURL();
      }
    });
    const files = {};
    Object.keys(values).forEach((k) => { if (values[k] instanceof File) files[k] = values[k]; });
    const meta = { mode, isEdit: !!initialData, hasFiles: Object.keys(files).length > 0, files, initialData };
    try {
      await onSubmit(values, meta);
      localStorage.removeItem(draftKey);
      setLastSavedAt(null);
    } finally {
      setIsSubmitting(false);
    }
  }

  function saveDraftNow() {
    try {
      const values = getValues();
      const serializable = {};
      Object.keys(values).forEach((k) => {
        const v = values[k];
        if (v instanceof File) serializable[k] = { __file_name: v.name };
        else serializable[k] = v;
      });
      localStorage.setItem(draftKey, JSON.stringify(serializable));
      setLastSavedAt(new Date().toISOString());
      alert("Draft saved locally");
    } catch (e) {
      console.warn(e);
      alert("Failed to save draft");
    }
  }

  function loadDraftNow() {
    const raw = localStorage.getItem(draftKey);
    if (!raw) return alert("No draft found");
    try {
      const draft = JSON.parse(raw);
      reset({ ...(initialData || {}), ...draft });
      alert("Draft loaded (files may need re-upload)");
    } catch (e) {
      console.warn(e);
      alert("Failed to load draft");
    }
  }

  function clearDraft() {
    localStorage.removeItem(draftKey);
    setLastSavedAt(null);
    alert("Draft cleared");
  }

  function exportDraft() {
    const raw = localStorage.getItem(draftKey) || JSON.stringify({});
    const blob = new Blob([raw], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${formId}-draft.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function importDraftFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        localStorage.setItem(draftKey, JSON.stringify(parsed));
        reset({ ...(initialData || {}), ...parsed });
        alert("Draft imported and loaded");
      } catch (err) {
        console.warn(err);
        alert("Invalid draft JSON");
      }
    };
    reader.readAsText(file);
  }

  async function handleDocxUpload(file) {
    if (!onSchemaGenerate) return alert("onSchemaGenerate not provided in props");
    try {
      const generated = await onSchemaGenerate(file);
      alert("Schema generated. Use it to initialise the form.");
      return generated;
    } catch (e) {
      console.error(e);
      alert("Failed to generate schema from docx");
    }
  }

  if (!schema) return <div className={styles.container}><div className={styles.formCard}><div className={styles.questionsContainer}>No schema provided</div></div></div>;

  return (
    <div className={styles.container}>
      <div className={styles.formCard}>
        <div className={styles.sectionHeader}>
          {schema.title && <h1 className={styles.sectionTitle}>{schema.title}</h1>}
          {schema.description && <p className={styles.sectionDescription}>{schema.description}</p>}
        </div>

        <div className={styles.questionsContainer}>
          {schema.sections.map((sec, sidx) => {
            if (!evalShowWhen(sec.showWhen, getValues())) return null;
            return (
              <div key={sec.id || sidx} className={styles.questionWrapper}>
                <div className={styles.questionHeader}>
                  <div className={styles.questionNumber}>{sidx + 1}</div>
                  <div style={{ flex: 1 }}>
                    <h3 className={styles.questionLabel}>{sec.title}</h3>
                    {sec.description && <div className={styles.helperText}>{sec.description}</div>}
                  </div>
                </div>
                <div>
                  {(sec.fields || []).map((f) => renderField(f))}
                </div>
              </div>
            );
          })}

          <div className={styles.navigationBar}>
            <div className={styles.buttonGroup}>
              <div>
                <button type="button" className={styles.backButton} onClick={saveDraftNow}>Save Draft</button>
                <button type="button" className={styles.backButton} onClick={loadDraftNow}>Load Draft</button>
                <button type="button" className={styles.backButton} onClick={clearDraft}>Clear Draft</button>
                <button type="button" className={styles.backButton} onClick={exportDraft}>Export Draft</button>
                <label className={styles.backButton} style={{ cursor: "pointer" }}>
                  Import Draft
                  <input type="file" accept="application/json" onChange={(e) => e.target.files[0] && importDraftFile(e.target.files[0])} style={{ display: "none" }} />
                </label>
                <label className={styles.backButton} style={{ cursor: "pointer" }}>
                  Upload Word (.docx)
                  <input type="file" accept=".docx" onChange={(e) => e.target.files[0] && handleDocxUpload(e.target.files[0])} style={{ display: "none" }} />
                </label>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className={styles.backButton} onClick={() => { }}>Preview</button>
                <button type="button" className={styles.nextButton} onClick={handleSubmit(submitHandler)} disabled={isSubmitting}>
                  {isSubmitting ? <span className={styles.spinner} /> : (mode === "edit" ? "Save" : "Create")}
                </button>
              </div>
            </div>
            <div style={{ marginTop: 8 }} className={styles.helperText}>
              {lastSavedAt ? <>Draft saved at: {(() => { const { formatDateTimeWithSeconds } = require('../../utils/dateFormatter'); return formatDateTimeWithSeconds(lastSavedAt); })()}</> : <>No draft saved</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------ final exported schema ------------------ */
export const formSchema = {
  title: "INSIGHTS Study - Case Report Form",
  description: "Cross-sectional pan-India study CRF",
  sections: [
    {
      id: "eligibility",
      title: "Eligibility & Investigator",
      description: "Inclusion/Exclusion criteria and investigator declaration",
      fields: [
        { type: "radio", name: "incl_criteria_1", label: "Patients ≥18 years of age", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}], required: true },
        { type: "radio", name: "incl_criteria_2", label: "Previously diagnosed with T2DM", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}], required: true },
        { type: "radio", name: "incl_criteria_3", label: "Willing to provide written informed consent", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}], required: true },

        { type: "radio", name: "excl_criteria_1", label: "Patients with T1DM and/or Gestational diabetes", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },
        { type: "radio", name: "excl_criteria_2", label: "Pregnant or lactating women or planning pregnancy", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },
        { type: "radio", name: "excl_criteria_3", label: "Participant in another clinical trial", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },

        { type: "text", name: "subject_eligible", label: "Subject Eligible for Study", compute: { formula: "({incl_criteria_1} === 'yes' && {incl_criteria_2} === 'yes' && {incl_criteria_3} === 'yes' && {excl_criteria_1} !== 'yes' && {excl_criteria_2} !== 'yes' && {excl_criteria_3} !== 'yes') ? 'yes' : 'no'" }, readOnly: true },

        { type: "signature", name: "investigator_signature", label: "Investigator Signature" },
        { type: "date", name: "investigator_signature_date", label: "Date" }
      ]
    },
    {
      id: "demographics",
      title: "Demographics, Lifestyle & Vitals",
      description: "Basic demographics, smoking/alcohol and vital signs",
      fields: [
        { type: "text", name: "age_years", label: "Age (in years)", required: true, placeholder: "e.g. 45" },
        { type: "radio", name: "gender", label: "Gender", required: true, options: [{label: "Male", value: "male"}, {label: "Female", value: "female"}] },
        { type: "text", name: "height_cm", label: "Height (cm)", placeholder: "e.g. 170" },
        { type: "text", name: "weight_kg", label: "Weight (kg)", placeholder: "e.g. 70" },
        { type: "text", name: "bmi", label: "Body Mass Index (Kg/m2)", compute: { formula: "({weight_kg} / (({height_cm}/100) * ({height_cm}/100)))" }, readOnly: true },
        { type: "text", name: "waist_cm", label: "Waist circumference (cm)" },
        { type: "text", name: "location_state", label: "Location / State" },

        { type: "select", name: "marital_status", label: "Marital Status", options: [
          {label: "Single", value: "single"}, {label: "Married", value: "married"}, {label: "Divorced/Separated", value: "divorced"}
        ]},

        { type: "select", name: "education_highest", label: "Educational Qualification (Highest)", options: [
          {label: "Illiterate", value: "illiterate"}, {label: "Matriculation or lower", value: "matric"}, {label: "Higher Secondary / Intermediate", value: "hsc"},
          {label: "Diploma / Vocational", value: "diploma"}, {label: "Graduate", value: "graduate"}, {label: "Postgraduate", value: "postgraduate"}, {label: "Professional / Doctoral", value: "professional"}
        ]},

        { type: "select", name: "employment_status", label: "Employment Status", options: [{label: "Employed", value: "employed"}, {label: "Unemployed", value: "unemployed"}] },
        { type: "select", name: "lifestyle", label: "Lifestyle", options: [{label: "Sedentary", value: "sedentary"}, {label: "Active", value: "active"}] },

        { type: "radio", name: "smoking_status", label: "Smoking Status", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },
        { type: "text", name: "smoke_start_date", label: "Smoking Start Date", showWhen: { field: "smoking_status", operator: "equals", value: "yes" } },
        { type: "text", name: "smoke_stop_date", label: "Smoking Stop Date", showWhen: { field: "smoking_status", operator: "equals", value: "yes" } },
        { type: "text", name: "smoke_frequency_per_day", label: "Smoking Frequency per day", showWhen: { field: "smoking_status", operator: "equals", value: "yes" } },

        { type: "radio", name: "alcohol_status", label: "Alcohol Consumption", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },
        { type: "text", name: "alcohol_start_date", label: "Alcohol Start Date", showWhen: { field: "alcohol_status", operator: "equals", value: "yes" } },
        { type: "text", name: "alcohol_stop_date", label: "Alcohol Stop Date", showWhen: { field: "alcohol_status", operator: "equals", value: "yes" } },
        { type: "text", name: "alcohol_quantity_ml", label: "Quantity per day (ml)", showWhen: { field: "alcohol_status", operator: "equals", value: "yes" } },

        { type: "text", name: "bp_systolic", label: "Blood Pressure Systolic (mmHg)", required: true },
        { type: "text", name: "bp_diastolic", label: "Blood Pressure Diastolic (mmHg)", required: true },
        { type: "text", name: "pulse", label: "Pulse (beats/min)" }
      ]
    },
    {
      id: "medical_history",
      title: "Diabetes, Medical & Family History",
      description: "Diabetes duration, comorbidities and family history",
      fields: [
        { type: "text", name: "diabetes_duration_years", label: "Duration of Diabetes - Years" },
        { type: "text", name: "diabetes_duration_months", label: "Duration of Diabetes - Months" },

        { type: "radio", name: "has_medical_history", label: "Any relevant medical history?", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },

        { type: "checkbox", name: "cond_obesity", label: "Obesity (BMI ≥25)", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_hypertension", label: "Hypertension", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_dyslipidemia", label: "Dyslipidemia", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_ckd", label: "Chronic Kidney Disease", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_steatotic_liver", label: "Steatotic Liver Disease", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_heart_failure", label: "Heart Failure", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_ascvd", label: "ASCVD", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_retinopathy", label: "Retinopathy", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "cond_neuropathy", label: "Neuropathy", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },
        { type: "text", name: "cond_others", label: "Others (please mention)", showWhen: { field: "has_medical_history", operator: "equals", value: "yes" } },

        { type: "radio", name: "parent_history", label: "Do any parents have medical history?", options: [{label: "Yes", value: "yes"}, {label: "No", value: "no"}] },
        { type: "checkbox", name: "fam_obesity", label: "Obesity (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_hypertension", label: "Hypertension (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_dyslipidemia", label: "Dyslipidemia (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_ckd", label: "Chronic Kidney Disease (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_steatotic_liver", label: "Steatotic Liver Disease (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_heart_failure", label: "Heart Failure (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_ascvd", label: "ASCVD (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_retinopathy", label: "Retinopathy (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } },
        { type: "checkbox", name: "fam_neuropathy", label: "Neuropathy (parent)", showWhen: { field: "parent_history", operator: "equals", value: "yes" } }
      ]
    },
    {
      id: "investigations_medications",
      title: "Investigations & Medications",
      description: "Lab values (last 6 months) and concomitant medication log",
      fields: [
        { type: "text", name: "lab_hba1c", label: "HbA1c" },
        { type: "text", name: "lab_fpg", label: "FPG" },
        { type: "text", name: "lab_ppg", label: "PPG" },
        { type: "text", name: "lab_tc", label: "Total Cholesterol (TC)" },
        { type: "text", name: "lab_ldl", label: "LDL-C" },
        { type: "text", name: "lab_hdl", label: "HDL-C" },
        { type: "text", name: "lab_non_hdl", label: "Non-HDL-C" },
        { type: "text", name: "lab_vldl", label: "VLDL-C" },
        { type: "text", name: "lab_tg", label: "Triglycerides (TG)" },
        { type: "text", name: "lab_egfr", label: "eGFR" },
        { type: "text", name: "lab_uacr", label: "UACR" },
        { type: "text", name: "lab_creatinine", label: "Serum Creatinine" },
        { type: "text", name: "lab_lvef", label: "LVEF (if HF suspected)" },
        { type: "text", name: "lab_ast", label: "AST" },
        { type: "text", name: "lab_alt", label: "ALT" },
        { type: "text", name: "lab_platelet", label: "Platelet Count" },
        { type: "text", name: "lab_fib4", label: "FIB-4 Score (calculated)" },

        {
          type: "array",
          name: "medications",
          label: "Concomitant Medications",
          itemFields: [
            { type: "select", name: "med_class", label: "Medication Class", options: [
              {label: "Antihyperglycemic", value: "antihyperglycemic"}, {label: "Antihypertensive", value: "antihypertensive"}, {label: "Lipid lowering", value: "lipid_lowering"},
              {label: "Antiplatelets", value: "antiplatelets"}, {label: "Heart Failure medication", value: "heart_failure"}, {label: "Antiobesity", value: "antiobesity"}, {label: "Other", value: "other"}
            ]},
            { type: "text", name: "med_name", label: "Medication Name" },
            { type: "text", name: "med_dose", label: "Dose" },
            { type: "select", name: "med_unit", label: "Unit", options: [
              {label: "g", value: "g"}, {label: "mg", value: "mg"}, {label: "µg", value: "mcg"}, {label: "tsp", value: "tsp"}, {label: "tbsp", value: "tbsp"}, {label: "U", value: "U"}, {label: "ml", value: "ml"}, {label: "mEq", value: "meq"}, {label: "IU", value: "iu"}
            ]},
            { type: "select", name: "med_form", label: "Dosage form", options: [
              {label: "Tablets", value: "tablets"}, {label: "Capsules", value: "capsules"}, {label: "Syrups", value: "syrups"}, {label: "Injection", value: "injection"}, {label: "Other", value: "other"}
            ]},
            { type: "select", name: "med_frequency", label: "Frequency", options: [
              {label: "OD", value: "od"}, {label: "QAM", value: "qam"}, {label: "Q24H", value: "q24h"}, {label: "PRN", value: "prn"}, {label: "BD", value: "bd"}, {label: "TID", value: "tid"}, {label: "QID", value: "qid"}, {label: "Others", value: "others"}
            ]},
            { type: "text", name: "med_duration_value", label: "Duration" },
            { type: "select", name: "med_duration_unit", label: "Duration Unit", options: [
              {label: "Days", value: "days"}, {label: "Months", value: "months"}, {label: "Years", value: "years"}
            ]}
          ]
        }
      ]
    },
    {
      id: "signoff",
      title: "Investigator Sign-off",
      description: "Final sign-off and investigator details",
      fields: [
        { type: "textarea", name: "investigator_notes", label: "Investigator Notes / Remarks" },
        { type: "signature", name: "final_investigator_signature", label: "Investigator Signature" },
        { type: "date", name: "final_signature_date", label: "Date" }
      ]
    }
  ]
};
